<template>
  <simple-dialog ref="dlg" title="New Survey Form" width="650" @ok="save">
    <template v-slot:content>
      <v-form>
        <v-text-field label="Title" v-model="form.title" />
        <v-textarea label="Description" v-model="form.description" rows="3" />
        <v-row>
          <v-col cols="12" md="6">
            <VueDatePicker
              v-model="form.startDate"
              :enable-time-picker="false"
              auto-apply
              format="dd/MMM/yyyy"
              placeholder="Start date"
            />
          </v-col>
          <v-col cols="12" md="6">
            <VueDatePicker
              v-model="form.endDate"
              :enable-time-picker="false"
              auto-apply
              format="dd/MMM/yyyy"
              placeholder="End date"
            />
          </v-col>
        </v-row>
        <v-checkbox v-model="form.isActive" label="Active" density="compact" hide-details />
        <v-checkbox v-model="form.isAllowMultipleEntries" label="Allow multiple entries" density="compact" hide-details />
        <v-checkbox v-model="form.isAllowUniqueAnswers" label="Allow unique answers" density="compact" hide-details />
        <div class="d-flex ga-2 mt-3">
          <v-btn size="small" variant="outlined" @click="openFilters">Filters ({{ activeFilterCount(filters) }})</v-btn>
          <v-btn size="small" variant="outlined" @click="openExclusions">Exclusions{{ hasExclusions(exclusions) ? ' (set)' : '' }}</v-btn>
        </div>
      </v-form>
    </template>
  </simple-dialog>

  <SurveyFiltersDialog ref="dlgFilters" v-model="filters" />
  <SurveyExclusionsDialog ref="dlgExclusions" v-model="exclusions" />
</template>

<script setup>
import { reactive, ref, inject } from "vue";
import { useRouter } from "vue-router";
import { createSurvey } from "@/services/surveyService";
import SurveyFiltersDialog from "./SurveyFiltersDialog.vue";
import SurveyExclusionsDialog from "./SurveyExclusionsDialog.vue";
import {
  emptySurveyFilters,
  filtersToRows,
  activeFilterCount,
} from "../utils/surveyFilterUtils";
import {
  emptySurveyExclusions,
  hasExclusions,
  exclusionsToPayload,
} from "../utils/surveyExclusionUtils";

const emit = defineEmits(["saved"]);

const router = useRouter();
const loader = inject("loader");
const snackbar = inject("snackBar");

const dlg = ref(null);
const dlgFilters = ref(null);
const dlgExclusions = ref(null);

const form = reactive({
  title: "",
  description: "",
  startDate: null,
  endDate: null,
  isActive: true,
  isAllowMultipleEntries: false,
  isAllowUniqueAnswers: false,
});

const filters = ref(emptySurveyFilters());
const exclusions = ref(emptySurveyExclusions());

function resetForm() {
  form.title = "";
  form.description = "";
  form.startDate = null;
  form.endDate = null;
  form.isActive = true;
  form.isAllowMultipleEntries = false;
  form.isAllowUniqueAnswers = false;
  filters.value = emptySurveyFilters();
  exclusions.value = emptySurveyExclusions();
}

function show() {
  resetForm();
  dlg.value.show();
}

function openFilters() {
  dlgFilters.value.show();
}

function openExclusions() {
  dlgExclusions.value.show();
}

function buildPayload() {
  return {
    title: form.title,
    description: form.description,
    startDate: form.startDate,
    endDate: form.endDate,
    isActive: form.isActive,
    isAllowMultipleEntries: form.isAllowMultipleEntries,
    isAllowUniqueAnswers: form.isAllowUniqueAnswers,
    questions: [],
    filters: filtersToRows(filters.value),
    ...exclusionsToPayload(exclusions.value),
  };
}

async function save() {
  if (!form.title?.trim()) {
    snackbar.error("Title is required");
    return;
  }
  if (!form.startDate || !form.endDate) {
    snackbar.error("Start and end dates are required");
    return;
  }

  loader.show();
  try {
    const result = await createSurvey(buildPayload());
    snackbar.success("Survey created");
    emit("saved");
    router.push(`/survey-forms/${result.surveyID}/edit`);
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

defineExpose({ show });
</script>
